import React, { useState, useEffect, useRef } from 'react';
import {
  BookOpen, Brain, Target, Shield, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, Zap, Eye, Search, Lock, Unlock,
  Terminal, Server, Bug, Activity, Sparkles, X, Maximize2, Minimize2,
  MessageSquare, Send, User, Bot, Loader2
} from 'lucide-react';
import useWebSocket from '../hooks/useWebSocket';
import { getScanWebSocketUrl } from '../utils/websocket';
import MarkdownRenderer from './common/MarkdownRenderer';

const AIStorytellingWindow = ({ scanId, target, isOpen, onClose }) => {
  const [storyEvents, setStoryEvents] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const storyContainerRef = useRef(null);
  const { lastMessage, isConnected } = useWebSocket(getScanWebSocketUrl(scanId));

  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatContainerRef = useRef(null);

  // Suggested questions
  const suggestedQuestions = [
    "Il reste combien d'IP à analyser ?",
    "Quelles vulnérabilités critiques ont été trouvées ?",
    "Quel est l'avancement du scan ?",
    "Combien de ports ouverts détectés ?"
  ];

  // Narrative templates for different event types
  const narrativeTemplates = {
    reconnaissance: [
      "Notre agent IA commence son investigation sur {target}...",
      "Tel un détective numérique, l'IA scrute les réseaux à la recherche d'indices...",
      "La première étape de notre mission : découvrir les portes d'entrée potentielles.",
    ],
    port_scan: [
      "L'IA frappe doucement à chaque porte du système, écoutant les réponses...",
      "Comme un cambrioleur évaluant une maison, nous identifions les points d'accès...",
      "Le scan des ports révèle {ports} ouvertures dans le périmètre de sécurité.",
    ],
    vulnerability_found: [
      "ALERTE ! Une brèche est détectée ! {vuln_name} pourrait être notre ticket d'entrée.",
      "L'IA a flairé une faiblesse : {vuln_name}. La cible se dévoile...",
      "Intéressant... Le système présente une vulnérabilité {severity}. Notons cela.",
    ],
    exploitation: [
      "L'heure de vérité. L'IA tente d'exploiter la faille découverte...",
      "Avec précision chirurgicale, notre agent déploie l'exploit adapté...",
      "Le moment crucial : transformer la théorie en accès concret.",
    ],
    access_gained: [
      "SUCCÈS ! L'IA a pénétré le système. La forteresse numérique cède.",
      "Nous sommes dans. L'accès est établi, la mission peut continuer.",
      "Comme prévu, la vulnérabilité était exploitable. Prochaine étape...",
    ],
    analysis: [
      "L'IA analyse méthodiquement les données collectées...",
      "Chaque information est pesée, évaluée, corrélée par notre agent intelligent.",
      "Le puzzle prend forme. L'IA relie les indices entre eux.",
    ],
    report: [
      "L'histoire touche à sa fin. L'IA compile ses découvertes...",
      "Le rapport final prend forme, chaque vulnérabilité documentée avec précision.",
      "Mission accomplie. Le verdict de sécurité est prêt.",
    ]
  };

  // Convert timeline events to narrative story
  const convertToStory = (event) => {
    const eventType = categorizeEvent(event);
    const templates = narrativeTemplates[eventType] || narrativeTemplates.analysis;
    const template = templates[Math.floor(Math.random() * templates.length)];

    return {
      id: event.id,
      chapter: getChapterName(eventType),
      chapterIcon: getChapterIcon(eventType),
      narrative: formatNarrative(template, event),
      technicalDetail: event.ai_output || event.thinking || event.stdout || event.description,
      timestamp: event.timestamp,
      type: eventType,
      severity: event.severity || 'info',
      status: event.status
    };
  };

  const categorizeEvent = (event) => {
    const title = (event.title || '').toLowerCase();
    const tool = (event.tool || '').toLowerCase();

    if (title.includes('reconnaissance') || title.includes('discovery') || tool.includes('nmap')) {
      return 'reconnaissance';
    }
    if (title.includes('port') || title.includes('scan')) {
      return 'port_scan';
    }
    if (title.includes('vuln') || title.includes('cve') || event.severity) {
      return 'vulnerability_found';
    }
    if (title.includes('exploit') || tool.includes('metasploit')) {
      return 'exploitation';
    }
    if (title.includes('access') || title.includes('shell') || title.includes('success')) {
      return 'access_gained';
    }
    if (title.includes('report')) {
      return 'report';
    }
    return 'analysis';
  };

  const getChapterName = (type) => {
    const chapters = {
      reconnaissance: 'Chapitre 1 : La Reconnaissance',
      port_scan: 'Chapitre 2 : L\'Exploration',
      vulnerability_found: 'Chapitre 3 : La Découverte',
      exploitation: 'Chapitre 4 : L\'Intrusion',
      access_gained: 'Chapitre 5 : La Pénétration',
      analysis: 'Chapitre 6 : L\'Analyse',
      report: 'Epilogue : Le Verdict'
    };
    return chapters[type] || 'Chapitre : Analyse en cours';
  };

  const getChapterIcon = (type) => {
    const icons = {
      reconnaissance: Search,
      port_scan: Eye,
      vulnerability_found: Bug,
      exploitation: Zap,
      access_gained: Unlock,
      analysis: Brain,
      report: BookOpen
    };
    return icons[type] || Activity;
  };

  const formatNarrative = (template, event) => {
    return template
      .replace('{target}', target || 'la cible')
      .replace('{ports}', event.data?.ports || 'plusieurs')
      .replace('{vuln_name}', event.title || 'une vulnerabilite')
      .replace('{severity}', event.severity || 'notable');
  };

  // Fetch initial timeline data with authentication for multi-tenant isolation
  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const backendUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${backendUrl}/api/v1/scan/${scanId}/timeline`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            const stories = data.map(event => convertToStory(event));
            setStoryEvents(stories);
          }
        } else if (response.status === 403) {
          console.warn('Access denied to scan timeline - multi-tenant isolation');
        }
      } catch (error) {
        console.error('Failed to fetch timeline:', error);
      }
    };

    if (scanId) {
      fetchTimeline();
      const interval = setInterval(fetchTimeline, 5000);
      return () => clearInterval(interval);
    }
  }, [scanId, target]);

  // Handle WebSocket messages
  useEffect(() => {
    if (lastMessage && lastMessage.scan_id === scanId && lastMessage.type === 'scan_event') {
      const newStory = convertToStory(lastMessage.event);
      setStoryEvents(prev => {
        const exists = prev.find(e => e.id === newStory.id);
        if (exists) {
          return prev.map(e => e.id === newStory.id ? newStory : e);
        }
        return [...prev, newStory];
      });
    }
  }, [lastMessage, scanId]);

  // Auto-scroll story
  useEffect(() => {
    if (autoScroll && storyContainerRef.current) {
      storyContainerRef.current.scrollTop = storyContainerRef.current.scrollHeight;
    }
  }, [storyEvents, autoScroll]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Send chat message
  const sendChatMessage = async (message) => {
    if (!message.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsTyping(true);

    try {
      const token = localStorage.getItem('access_token');
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/v1/ai/scan/${scanId}/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ question: message })
      });

      if (response.ok) {
        const data = await response.json();
        const aiMessage = {
          id: Date.now() + 1,
          type: 'ai',
          content: data.response,
          stats: data.scan_stats,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, aiMessage]);
      } else {
        const aiMessage = {
          id: Date.now() + 1,
          type: 'ai',
          content: "Desole, je n'ai pas pu traiter votre question. Veuillez reessayer.",
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "Erreur de connexion. Veuillez reessayer.",
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, aiMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage(chatInput);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-500 border-red-500';
      case 'high': return 'text-orange-500 border-orange-500';
      case 'medium': return 'text-yellow-500 border-yellow-500';
      case 'low': return 'text-blue-400 border-blue-400';
      default: return 'text-brand-green border-brand-green';
    }
  };

  const getTypeGradient = (type) => {
    switch (type) {
      case 'reconnaissance': return 'from-blue-500/20 to-cyan-500/20';
      case 'port_scan': return 'from-cyan-500/20 to-teal-500/20';
      case 'vulnerability_found': return 'from-red-500/20 to-orange-500/20';
      case 'exploitation': return 'from-purple-500/20 to-pink-500/20';
      case 'access_gained': return 'from-green-500/20 to-emerald-500/20';
      case 'report': return 'from-brand-green/20 to-green-500/20';
      default: return 'from-gray-500/20 to-gray-600/20';
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed ${isExpanded ? 'inset-4' : 'bottom-4 right-4 w-[500px] h-[600px]'} z-50 transition-all duration-300`}>
      <div className="h-full bg-brand-black-light rounded-2xl border border-brand-green/30 shadow-2xl shadow-brand-green/10 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-brand-black to-brand-black-light">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-black" />
              </div>
              <div>
                <h3 className="font-bold text-white flex items-center gap-2">
                  Recit de l'IA
                  {isConnected && (
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  )}
                </h3>
                <p className="text-xs text-gray-500">L'histoire de votre pentest en temps reel</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowChat(!showChat)}
                className={`p-2 rounded-lg transition-colors ${showChat ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-800 text-gray-400 hover:bg-purple-500/10'}`}
                title={showChat ? 'Voir le recit' : 'Poser une question'}
              >
                <MessageSquare className="w-4 h-4" />
              </button>
              <button
                onClick={() => setAutoScroll(!autoScroll)}
                className={`p-2 rounded-lg transition-colors ${autoScroll ? 'bg-brand-green/20 text-brand-green' : 'bg-gray-800 text-gray-400'}`}
                title={autoScroll ? 'Defilement auto actif' : 'Defilement auto desactive'}
              >
                <Activity className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                {isExpanded ? <Minimize2 className="w-4 h-4 text-gray-400" /> : <Maximize2 className="w-4 h-4 text-gray-400" />}
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-gray-800 rounded-lg hover:bg-red-500/20 transition-colors"
              >
                <X className="w-4 h-4 text-gray-400 hover:text-red-400" />
              </button>
            </div>
          </div>

          {/* Tab indicator */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setShowChat(false)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${!showChat ? 'bg-brand-green/20 text-brand-green border border-brand-green/30' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            >
              <div className="flex items-center justify-center gap-2">
                <BookOpen className="w-4 h-4" />
                Recit
              </div>
            </button>
            <button
              onClick={() => setShowChat(true)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${showChat ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            >
              <div className="flex items-center justify-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Questions
              </div>
            </button>
          </div>
        </div>

        {/* Content area - Story or Chat */}
        {!showChat ? (
          /* Story Content */
          <div
            ref={storyContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4"
            onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              if (scrollHeight - scrollTop > clientHeight + 50) {
                setAutoScroll(false);
              }
            }}
          >
            {/* Prologue */}
            <div className="bg-gradient-to-r from-brand-green/10 to-transparent rounded-xl p-4 border border-brand-green/30">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-brand-green" />
                <h4 className="font-bold text-brand-green">Prologue</h4>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed italic">
                "Il était une fois, dans les profondeurs du cyberespace, une cible nommée <span className="text-brand-green font-semibold">{target}</span>.
                Notre agent IA, armé de sa connaissance du framework MITRE ATT&CK, s'apprête à révéler les secrets de sa sécurité..."
              </p>
            </div>

            {/* Story Events */}
            {storyEvents.map((story, index) => {
              const ChapterIcon = story.chapterIcon;
              const isNewChapter = index === 0 || story.chapter !== storyEvents[index - 1]?.chapter;

              return (
                <div key={story.id} className="space-y-2">
                  {/* Chapter Header */}
                  {isNewChapter && (
                    <div className="flex items-center gap-3 py-2">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
                      <div className="flex items-center gap-2 px-3 py-1 bg-gray-800/50 rounded-full">
                        <ChapterIcon className={`w-4 h-4 ${getSeverityColor(story.severity).split(' ')[0]}`} />
                        <span className="text-xs font-medium text-gray-400">{story.chapter}</span>
                      </div>
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
                    </div>
                  )}

                  {/* Story Card */}
                  <div className={`bg-gradient-to-r ${getTypeGradient(story.type)} rounded-xl border border-gray-800 overflow-hidden`}>
                    <div className="p-4">
                      {/* Narrative Text */}
                      <p className="text-white leading-relaxed mb-3">
                        <span className="text-brand-green font-bold text-2xl leading-none mr-1">"</span>
                        {story.narrative}
                        <span className="text-brand-green font-bold text-2xl leading-none ml-1">"</span>
                      </p>

                      {/* Technical Details Toggle */}
                      {story.technicalDetail && (
                        <details className="group">
                          <summary className="flex items-center gap-2 cursor-pointer text-xs text-gray-500 hover:text-gray-300 transition-colors list-none">
                            <Terminal className="w-3 h-3" />
                            <span>Details techniques</span>
                            <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                          </summary>
                          <div className="mt-2 p-3 bg-gray-900/50 rounded-lg text-xs text-gray-400 max-h-48 overflow-y-auto">
                            <MarkdownRenderer content={story.technicalDetail} variant="compact" />
                          </div>
                        </details>
                      )}

                      {/* Timestamp */}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800/50">
                        <div className="flex items-center gap-2">
                          {story.status === 'completed' && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <CheckCircle className="w-3 h-3" />
                              Complete
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-600">
                          {new Date(story.timestamp).toLocaleTimeString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Waiting indicator */}
            {storyEvents.length > 0 && storyEvents[storyEvents.length - 1]?.status === 'processing' && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Brain className="w-5 h-5 text-purple-400 animate-pulse" />
                <span className="text-sm text-gray-400">L'IA ecrit la suite de l'histoire...</span>
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            )}

            {/* Empty state */}
            {storyEvents.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mb-4">
                  <BookOpen className="w-8 h-8 text-brand-green" />
                </div>
                <h4 className="text-white font-medium mb-2">L'histoire n'a pas encore commence</h4>
                <p className="text-sm text-gray-500 max-w-xs">
                  Lancez un scan pour voir l'IA vous raconter son investigation en temps reel
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Chat Content */
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Chat messages */}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {/* Welcome message if no messages */}
              {chatMessages.length === 0 && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-purple-500/10 to-transparent rounded-xl p-4 border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="w-5 h-5 text-purple-400" />
                      <h4 className="font-bold text-purple-400">Assistant IA</h4>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Bonjour ! Je suis l'assistant IA de SYLink. Posez-moi des questions sur le scan en cours :
                      nombre d'hôtes, vulnérabilités, progression, etc.
                    </p>
                  </div>

                  {/* Suggested questions */}
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 px-1">Questions suggérées :</p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => sendChatMessage(q)}
                          className="px-3 py-1.5 text-xs bg-gray-800 text-gray-300 rounded-lg hover:bg-purple-500/20 hover:text-purple-300 transition-colors border border-gray-700 hover:border-purple-500/30"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Chat messages */}
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.type === 'ai' && (
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-purple-400" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-xl p-3 ${
                      msg.type === 'user'
                        ? 'bg-brand-green/20 text-white border border-brand-green/30'
                        : 'bg-gray-800 text-gray-200 border border-gray-700'
                    }`}
                  >
                    {msg.type === 'ai' ? (
                      <MarkdownRenderer content={msg.content} variant="chat" />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                    <span className="text-xs text-gray-500 mt-2 block">
                      {new Date(msg.timestamp).toLocaleTimeString('fr-FR')}
                    </span>
                  </div>
                  {msg.type === 'user' && (
                    <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-brand-green" />
                    </div>
                  )}
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="bg-gray-800 rounded-xl p-3 border border-gray-700">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                      <span className="text-sm text-gray-400">L'IA reflechit...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat input */}
            <div className="p-3 border-t border-gray-800 bg-brand-black/50">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Posez votre question..."
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/30"
                  disabled={isTyping}
                />
                <button
                  onClick={() => sendChatMessage(chatInput)}
                  disabled={isTyping || !chatInput.trim()}
                  className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-purple-500/30"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Stats */}
        <div className="p-3 border-t border-gray-800 bg-brand-black/50">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{storyEvents.length} evenement(s) dans l'histoire</span>
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3 text-brand-green" />
              Analyse autonome par IA
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIStorytellingWindow;
